import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import FormField from "../components/ui/FormField";
import "./Organisers.css";
import axios from "axios";
import React, { useState, useEffect } from "react";
import useAuth from "../hooks/useAuth";
import { FiRefreshCw } from "react-icons/fi";

export default function Organisers() {
  const [form, setForm] = useState({
    organiser_name: "",
    mobile: "",
    email: "",
    address: "",
    district: "",
    state: "",
    bank_account: "",
    commission_pct: 0,
    is_active: 1,
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [organisers, setOrganisers] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const { refreshToken, user } = useAuth();
  const [editingId, setEditingId] = useState(null);

  // ✅ LOAD FROM SESSION
  const fetchOrganisers = async () => {
    try {
      setLoadingList(true);

      const token = sessionStorage.getItem("maaze_token");
      const storedUser = JSON.parse(sessionStorage.getItem("maaze_user"));

      if (!storedUser?.organiser_id) {
        setMessage("Organiser ID not found. Please re-login.");
        return;
      }

      const response = await axios.get(
        `https://maize.norissolutions.in/Php/public/api/organisers/${storedUser.organiser_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            season_id: storedUser.season_id,
          },
        }
      );

      const data = response.data?.data?.[0];

      if (!data) {
        setMessage("No organiser data found");
        return;
      }

      setOrganisers([data]);

      setForm({
        organiser_name: data.organiser_name,
        mobile: data.mobile,
        email: data.email || "",
        address: data.address || "",
        district: data.district || "",
        state: data.state || "",
        bank_account: data.bank_account || "",
        commission_pct: data.commission_pct || 0,
        is_active: data.is_active,
      });

      setEditingId(data.organiser_id);

      // 🔥 store latest organiser
      sessionStorage.setItem("maaze_organiser", JSON.stringify(data));

    } catch (err) {
      console.error(err);
      setMessage("Failed to fetch organiser");
    } finally {
      setLoadingList(false);
    }
  };

  // ✅ UPDATE API
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setMessage("");

      if (!editingId) {
        setMessage("No organiser selected for update");
        return;
      }

      const token = sessionStorage.getItem("maaze_token");

      const payload = {
        organiser_name: form.organiser_name,
        email: form.email,
        mobile: form.mobile,
        address: form.address,
        district: form.district,
        state: form.state,
        bank_account: form.bank_account,
        commission_pct: Number(form.commission_pct),
        is_active: form.is_active,
      };

      // 🔥 PUT API (FIXED)
      const response = await axios.put(
        `https://maize.norissolutions.in/Php/public/api/organisers/${editingId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setMessage(response.data.message || "Updated successfully");

      // 🔥 UPDATE SESSION (IMPORTANT)
      const existing = JSON.parse(sessionStorage.getItem("maaze_organiser"));

      const updated = {
        ...existing,
        ...payload,
      };

      sessionStorage.setItem(
        "maaze_organiser",
        JSON.stringify(updated)
      );

      // 🔥 UPDATE UI (no API call needed)
      setOrganisers([updated]);

    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganisers();
  }, []);

  return (
    <div>
      <PageHeader
        title="Organisers"
        subtitle="Hierarchy: Organiser → Villages → Farmers"
      >
        <FiRefreshCw className="page-refresh" onClick={refreshToken} />
      </PageHeader>

      <div className="grid-2">
        {/* LEFT */}
        <Card title="Organiser Network">
          <div className="org-tree">
            {organisers.map((org, index) => (
              <div className="org-node" key={index}>
                <div className="org-node__header">
                  <span className="org-node__icon">🏢</span>
                  <span className="org-node__name">{org.organiser_name}</span>
                  <span className="org-node__badge">
                    {org.is_active === 1 ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* RIGHT FORM */}
        <Card title="Update Organiser">
          {message && (
            <div className="message-banner">{message}</div>
          )}

          <div className="form-grid--double">
            <FormField
              label="Full Name"
              type="text"
              value={form.organiser_name}
              readOnly
              onChange={() => { }}
              className="readonly-field"
            />

            <FormField
              label="Mobile"
              type="tel"
              value={form.mobile}
              onChange={(e) =>
                setForm({ ...form, mobile: e.target.value })
              }
            />

            <FormField
              label="Email"
              type="email"
              value={form.email}
              readOnly
              onChange={() => { }}
              className="readonly-field"
            />

            <FormField
              label="Address"
              type="text"
              value={form.address}
              onChange={(e) =>
                setForm({ ...form, address: e.target.value })
              }
            />

            <FormField
              label="District"
              type="text"
              value={form.district}
              onChange={(e) =>
                setForm({ ...form, district: e.target.value })
              }
            />

            <FormField
              label="State"
              type="text"
              value={form.state}
              onChange={(e) =>
                setForm({ ...form, state: e.target.value })
              }
            />

            <FormField
              label="Commission (%)"
              type="number"
              value={form.commission_pct}
              onChange={(e) =>
                setForm({ ...form, commission_pct: e.target.value })
              }
            />

            <FormField
              label="Bank Account"
              type="text"
              value={form.bank_account}
              onChange={(e) =>
                setForm({ ...form, bank_account: e.target.value })
              }
            />

            <FormField
              label="Active Status"
              type="select"
              options={[
                { label: "Active", value: 1 },
                { label: "Inactive", value: 0 },
              ]}
              value={form.is_active}
              onChange={(e) =>
                setForm({
                  ...form,
                  is_active: Number(e.target.value),
                })
              }
            />
          </div>

          <Button
            style={{ marginTop: 16 }}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Organiser"}
          </Button>
        </Card>
      </div>
    </div>
  );
}